import Phaser from 'phaser';
import { Palette } from '@core/config/constants';
import { AUDIO_REGISTRY_KEY } from '@core/scenes/BaseScene';
import type { AudioService } from '@core/services/AudioService';

export interface CycleSelectorOptions {
  x: number;
  y: number;
  title: string;
  /** Etiquetas de cada opcion; la cantidad define el rango del indice. */
  labels: readonly string[];
  /** Se llama con el nuevo indice cada vez que cambia la seleccion. */
  onChange: (index: number) => void;
  width?: number;
  /** Indice inicial a mostrar (no dispara onChange). Por defecto 0. */
  startIndex?: number;
}

const DEFAULT_WIDTH = 360;
const ARROW_SIZE = 56;
const TITLE_OFFSET_Y = -46;

/**
 * Selector ciclico reutilizable: muestra un titulo, el valor actual y dos flechas
 * (◀ ▶) para recorrer las opciones. Encapsula su propio estado de indice y avisa
 * al exterior mediante onChange. Evita repetir logica de flechas en cada escena.
 */
export class CycleSelector extends Phaser.GameObjects.Container {
  private index = 0;
  private readonly labels: readonly string[];
  private readonly valueText: Phaser.GameObjects.Text;
  private readonly onChange: (index: number) => void;

  public constructor(scene: Phaser.Scene, options: CycleSelectorOptions) {
    super(scene, options.x, options.y);
    this.labels = options.labels;
    this.onChange = options.onChange;

    const total = this.labels.length;
    const start = options.startIndex ?? 0;
    this.index = total > 0 ? ((start % total) + total) % total : 0;

    const width = options.width ?? DEFAULT_WIDTH;

    const title = scene.add
      .text(0, TITLE_OFFSET_Y, options.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.valueText = scene.add
      .text(0, 0, this.labels[this.index] ?? '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '28px',
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: width - ARROW_SIZE * 2 },
      })
      .setOrigin(0.5);

    const leftArrow = this.createArrow(scene, -width / 2, '◀', -1);
    const rightArrow = this.createArrow(scene, width / 2, '▶', 1);

    this.add([title, this.valueText, leftArrow, rightArrow]);
    scene.add.existing(this);
  }

  private createArrow(
    scene: Phaser.Scene,
    x: number,
    symbol: string,
    direction: number,
  ): Phaser.GameObjects.Text {
    const arrow = scene.add
      .text(x, 0, symbol, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: `${ARROW_SIZE}px`,
        color: Palette.textLight,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    arrow.on(Phaser.Input.Events.POINTER_UP, () => this.cycle(direction));
    return arrow;
  }

  private cycle(direction: number): void {
    const total = this.labels.length;
    if (total === 0) {
      return;
    }
    this.index = (this.index + direction + total) % total;
    this.valueText.setText(this.labels[this.index]);
    const audio = this.scene.registry.get(AUDIO_REGISTRY_KEY) as AudioService | undefined;
    audio?.playClick();
    this.onChange(this.index);
  }
}
